import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { inspectionAPI } from '../../services/api';
import toast from 'react-hot-toast';
import InspectionList from '../../components/inspection/InspectionList';
import InspectionForm from '../../components/inspection/InspectionForm';
import Modal from '../../components/common/Modal';

const InspectionPage = () => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);
  
  const queryClient = useQueryClient();

  // 获取巡检列表
  const { data, isLoading, error } = useQuery(
    ['inspections', { status: statusFilter, search, page }],
    () => inspectionAPI.getAll({
      status: statusFilter,
      search,
      page,
      per_page: 10
    })
  );

  // 创建巡检
  const createMutation = useMutation(
    (data) => inspectionAPI.create(data),
    {
      onSuccess: () => {
        toast.success('巡检任务创建成功');
        setShowCreateModal(false);
        queryClient.invalidateQueries('inspections');
        queryClient.invalidateQueries('dashboardOverview');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || '创建失败');
      }
    }
  );

  // 更新巡检
  const updateMutation = useMutation(
    ({ id, data }) => inspectionAPI.update(id, data),
    {
      onSuccess: () => {
        toast.success('巡检任务更新成功');
        setSelectedInspection(null);
        queryClient.invalidateQueries('inspections');
        queryClient.invalidateQueries('dashboardOverview');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || '更新失败');
      }
    }
  );

  // 删除巡检
  const deleteMutation = useMutation(
    (id) => inspectionAPI.delete(id),
    {
      onSuccess: () => {
        toast.success('巡检任务删除成功');
        queryClient.invalidateQueries('inspections');
        queryClient.invalidateQueries('dashboardOverview');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || '删除失败');
      }
    }
  );

  const handleCreate = (data) => {
    createMutation.mutate(data);
  };

  const handleUpdate = (id, data) => {
    updateMutation.mutate({ id, data });
  };

  const handleDelete = (id) => {
    if (window.confirm('确定要删除这个巡检任务吗？')) {
      deleteMutation.mutate(id);
    }
  };

  const handleStatusChange = (id, newStatus) => {
    updateMutation.mutate({ id, data: { status: newStatus } });
  };

  const inspections = data?.inspections || [];
  const pagination = data ? {
    total: data.total,
    page: data.page,
    per_page: data.per_page,
    pages: data.pages
  } : null;

  return (
    <div className="space-y-6">
      {/* 页面标题和操作按钮 */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">安全巡检管理</h1>
          <p className="text-gray-600 mt-1">管理安全巡检任务和检查记录</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="mt-4 sm:mt-0 btn-primary"
        >
          <i className="fa fa-plus mr-2"></i>
          新建巡检任务
        </button>
      </div>

      {/* 搜索和过滤 */}
      <div className="bg-white p-4 rounded-lg shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
          <div className="flex-1">
            <div className="relative">
              <input
                type="text"
                placeholder="搜索巡检任务..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="form-input pl-10"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i className="fa fa-search text-gray-400"></i>
              </div>
            </div>
          </div>
          <div className="w-full sm:w-auto">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="form-input"
            >
              <option value="">所有状态</option>
              <option value="pending">待下发</option>
              <option value="processing">待处理</option>
              <option value="reviewing">待审批</option>
              <option value="completed">已完成</option>
              <option value="archived">已归档</option>
            </select>
          </div>
        </div>
      </div>

      {/* 巡检列表 */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="spinner mx-auto"></div>
            <p className="mt-4 text-gray-600">加载巡检任务中...</p>
          </div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <i className="fa fa-exclamation-circle text-red-500 mr-3"></i>
            <div>
              <p className="text-red-800 font-medium">加载失败</p>
              <p className="text-red-600 text-sm mt-1">无法加载巡检任务列表，请稍后重试</p>
            </div>
          </div>
        </div>
      ) : (
        <InspectionList
          inspections={inspections}
          onEdit={setSelectedInspection}
          onDelete={handleDelete}
          onStatusChange={handleStatusChange}
          pagination={pagination}
          onPageChange={setPage}
        />
      )}

      {/* 创建巡检模态框 */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="新建巡检任务"
      >
        <InspectionForm
          onSubmit={handleCreate}
          onCancel={() => setShowCreateModal(false)}
          isLoading={createMutation.isLoading}
        />
      </Modal>

      {/* 编辑巡检模态框 */}
      <Modal
        isOpen={!!selectedInspection}
        onClose={() => setSelectedInspection(null)}
        title="编辑巡检任务"
      >
        {selectedInspection && (
          <InspectionForm
            inspection={selectedInspection}
            onSubmit={(data) => handleUpdate(selectedInspection.id, data)}
            onCancel={() => setSelectedInspection(null)}
            isLoading={updateMutation.isLoading}
          />
        )}
      </Modal>
    </div>
  );
};

export default InspectionPage;